import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';

import {
  ItemDTO, CatalogItemShortDTO,
} from '@/api';
import service from '@/services';
import { throwError } from '@/utils';

export function getTempRange(thing: ItemDTO): [number, number] {
  return [Number(thing.temperatureFrom), Number(thing.temperatureTo)];
}

@Module({ name: 'SandboxModule', namespaced: true })
export default class SandboxModule extends VuexModule {
  loading = false;

  sandboxThings: CatalogItemShortDTO[] = [];

  userThings: ItemDTO[] = [];

  get things() {
    return [...this.sandboxThings, ...this.userThings];
  }

  get userThingsCategories() {
    return this.userThings.reduce<string[]>((acc, thing) => {
      const { category } = thing;
      return category && !acc.includes(category)
        ? acc.concat(category)
        : acc;
    }, []);
  }

  get groupByCategory() {
    const categories = [
      ...this.userThingsCategories.map(category => ({
        name: category,
        type: 'default',
        condition: (thing: ItemDTO) => thing.category === category,
        priority: 1,
        order: 1,
        rows: [] as (ItemDTO)[],
      })),
    ];
    const byPriority = categories.slice().sort((a, b) => b.priority - a.priority);
    const byOrder = categories.slice().sort((a, b) => a.order - b.order);
    this.userThings.forEach((thing) => {
      const condition = byPriority.find(category => category.condition(thing));
      if (condition) {
        condition.rows.push(thing);
      }
    });
    return byOrder.filter(c => c.rows.length);
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  @Mutation
  setSandboxThings(list: CatalogItemShortDTO[]) {
    this.sandboxThings = list;
  }

  @Mutation
  setUserThings(list: ItemDTO[]) {
    this.userThings = list;
  }

  @Mutation
  removeSandboxThing(catalogItemId: number) {
    const index = this.sandboxThings.findIndex(thing => thing.id === catalogItemId);
    if (index > -1) {
      this.sandboxThings.splice(index, 1);
    }
  }

  @Action({ rawError: true })
  async fetchSandboxThings(userId: number) {
    try {
      this.setSandboxThings([]);
      amplitude.getInstance().logEvent('Загрузка вещей в песочнице');
      this.setLoading(true);
      const { data } = await service.sandboxApi().sandboxEndpointGetItems(userId);
      this.setSandboxThings(data);
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки списка вещей в песочнице')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async fetchUserThings(clientId: number) {
    try {
      amplitude.getInstance().logEvent('Загрузка вещей клиента');
      this.setLoading(true);
      const { data } = await service.looksApi().lookEndpointGetThings(clientId);
      this.setUserThings(data);
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки списка вещей клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async fetchThings(clientId: number) {
    try {
      const data = Promise.all([
        this.fetchSandboxThings(clientId),
        this.fetchUserThings(clientId),
      ]);
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки списка вещей клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addToUserThings({ clientId, sandBoxItemIds }:
      {clientId: number, sandBoxItemIds: number[]}) {
    try {
      amplitude.getInstance().logEvent('Перенос из песочницы в вещи клиента');
      await service.sandboxApi().sandboxEndpointMoveThingsToClient(clientId, sandBoxItemIds);
      sandBoxItemIds.forEach((id) => {
        this.removeSandboxThing(id);
      });
      return { clientId, sandBoxItemIds };
    } catch (e) {
      return throwError('Ошибка переноса из песочницы в вещи клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addToUserLooks({ clientId, sandBoxLookIds }: {clientId: number, sandBoxLookIds: number[]}) {
    try {
      amplitude.getInstance().logEvent('Перенос из песочницы в образы клиента');
      await service.sandboxApi().sandboxEndpointMoveLooksToClient(clientId, sandBoxLookIds);
      this.fetchSandboxThings(clientId);
      return { clientId, sandBoxLookIds };
    } catch (e) {
      return throwError('Ошибка переноса из песочницы в образы клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async deleteSandboxThings({ clientId, items }:
    {clientId: number, items: {catalogItemId: number, priceId: number}[]}) {
    try {
      amplitude.getInstance().logEvent('Удаление вещи из песочницы');
      service.sandboxApi().sandboxEndpointRemoveItems(clientId, items);
      items.forEach(({ catalogItemId }) => this.removeSandboxThing(catalogItemId));

      return { clientId };
    } catch (e) {
      return throwError('Ошибка удаления вещи из песочницы')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async improveImage({ clientId, thingId, imageUrl }:
      { clientId: number; thingId: number; imageUrl: string }) {
    try {
      await service.catalogService()
        .catalogEndpointSetItemPictureFromUrl(thingId, imageUrl);
      await this.fetchSandboxThings(clientId);
      return imageUrl;
    } catch (e) {
      return throwError('Ошибка обновления изображения')(e);
    }
  }
}
